import classnames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { BackTitle } from '@meitu/saas-ui/es/title';
import { events, HIDE_COLLAPSE_MOG } from '@/events-bus';
import { useFeatureHelper } from '@/hooks/use-feature-helper';
import { useLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { ImagePanelFeatureType } from '@/types';
import { TRACK_CONFIG } from '../track-config';
import { IImgPanelType } from '../type';
import AdjustItemWrap from './components/adjust-item-wrap';
import AdjustTagList from './components/adjust-tag-list';
import { getTrackFunc, useCardMenuTrack } from './hooks/use-card-menu-track';
import { useEnhanceEventsHelper } from './hooks/use-enhance-helper';
import BaseEnhance from './widgets/base-enhance';
import ColorEnhance from './widgets/color-enhance';
import HslEnhance from './widgets/hsl-enhance';
import LightEnhance from './widgets/light-enhance';
import ParticularsEnhance from './widgets/particulars-enhance';
import PosterizeEnhance from './widgets/posterize-enhance';
import './image-adjust.less';
import { useImagePanelAdjustModel } from '@/store/panel-models/image-panel-adjust-model';

interface ImageAdjustProps {
    layerId: string;
    setPanel: (type: IImgPanelType | null) => void;
}

export const HIDE_PANEL_ADJUST = 'HIDE_PANEL_ADJUST';

const ImageAdjust: React.FC<ImageAdjustProps> = ({ layerId, setPanel }) => {
    const { $t } = useLocaleStore();

    const curType = useImagePanelAdjustModel((state) => state.currentType);
    const setCurType = useImagePanelAdjustModel((state) => state.setCurrentType);

    const isPhoto = layerId ? editor.imageLayer.checkIsPhoto(layerId) : false;
    const isAiText = layerId ? editor.imageLayer.checkIsAiText(layerId) : false;
    const firstId = isPhoto ? 4 : isAiText ? 6 : 2;

    const [advancedType, setAdvancedType] = useState<ImagePanelFeatureType | ''>('');

    const { use, getCurFeature } = useFeatureHelper();
    const { onToggleHandle, onSliderHandle } = useCardMenuTrack(TRACK_CONFIG, `${firstId}`);

    useEffect(() => {
        use(IImgPanelType.ADJUST);
        // 默认使用基础调色
        setCurType(ImagePanelFeatureType.ADJUST_BASE);
        onToggleHandle(ImagePanelFeatureType.ADJUST_BASE, true);

        return () => {
            use('');
            setCurType('');
        };
    }, []);

    // 接收关闭 Adjust 事件, collapse 与 panel 两个调整功能互斥
    useEffect(() => {
        // 调色 collapse 与 panel 两个调整功能互斥
        events.emit(HIDE_COLLAPSE_MOG);

        const handleHideAdjust = () => {
            editor.imageEnhance.reset();
            setPanel(null);
        };

        events.on(HIDE_PANEL_ADJUST, handleHideAdjust);

        return () => {
            events.off(HIDE_PANEL_ADJUST, handleHideAdjust);
        };
    }, []);

    useEnhanceEventsHelper(IImgPanelType.ADJUST, curType, layerId);

    const trackFunc = useCallback((curType) => {
        return curType ? getTrackFunc(TRACK_CONFIG[curType]) : undefined;
    }, []);

    const onShowChange = useCallback(
        (cardType: ImagePanelFeatureType | '', isBaseType: boolean, isShow: boolean, e: React.MouseEvent) => {
            console.log({ cardType, isBaseType, isShow, advancedType, e });

            if (isBaseType) {
                if (isShow) {
                    setCurType(ImagePanelFeatureType.ADJUST_BASE);
                    // setTempType(cardType);
                    // onToggleHandle(cardType, true);
                } else {
                    setCurType('');
                    // onToggleHandle(cardType, false);
                }
            } else {
                if (isShow) {
                    if (advancedType) {
                        setCurType(advancedType);
                        return;
                    }
                    setCurType(ImagePanelFeatureType.ADJUST_LIGHT);
                    setAdvancedType(ImagePanelFeatureType.ADJUST_LIGHT);
                    // onToggleHandle(tempType, true);
                } else {
                    setCurType('');
                    setAdvancedType(cardType);
                    // onToggleHandle(cardType, false);
                }
            }
        },
        [advancedType],
    );

    return (
        <div className={classnames('c-image-mix-panel')}>
            <div className="title">
                <BackTitle
                    text={$t((v) => v.panel.imagePanel.imageAdjust)}
                    handleBackClick={() => {
                        setTimeout(() => {
                            setPanel(null);
                        }, 0);
                    }}
                />
            </div>

            <AdjustItemWrap
                name={$t((v) => v.panel.imagePanel.baseImageAdjust)}
                icon="PaletteBold"
                cardTypeList={[ImagePanelFeatureType.ADJUST_BASE]}
                curType={curType}
                setCurType={setCurType}
                isBaseType={true}
                onShowChange={onShowChange}
            >
                <BaseEnhance
                    featureType={ImagePanelFeatureType.ADJUST_BASE}
                    curType={curType}
                    setCurType={setCurType}
                    // onToggle={onToggleHandle}
                    // onReset={onResetHandle}
                    onSlider={onSliderHandle}
                    trackFunc={trackFunc(ImagePanelFeatureType.ADJUST_BASE)}
                />
            </AdjustItemWrap>

            <AdjustItemWrap
                name={$t((v) => v.panel.imagePanel.advancedImageAdjust)}
                icon="HslHueBold"
                cardTypeList={[
                    ImagePanelFeatureType.ADJUST_LIGHT,
                    ImagePanelFeatureType.ADJUST_COLOR,
                    ImagePanelFeatureType.ADJUST_PARTICULARS,
                    ImagePanelFeatureType.ADJUST_POSTERIZE,
                    ImagePanelFeatureType.ADJUST_HSL,
                ]}
                curType={curType}
                setCurType={setCurType}
                onShowChange={onShowChange}
            >
                <AdjustTagList
                    cardTypeList={[
                        ImagePanelFeatureType.ADJUST_LIGHT,
                        ImagePanelFeatureType.ADJUST_COLOR,
                        ImagePanelFeatureType.ADJUST_PARTICULARS,
                        ImagePanelFeatureType.ADJUST_POSTERIZE,
                        ImagePanelFeatureType.ADJUST_HSL,
                    ]}
                    cardTagList={[
                        $t((v) => v.panel.imagePanel.adjustLight),
                        $t((v) => v.panel.imagePanel.adjustColor),
                        $t((v) => v.panel.imagePanel.adjustParticulars),
                        $t((v) => v.panel.imagePanel.adjustPosterize),
                        $t((v) => v.panel.imagePanel.adjustHsl),
                    ]}
                    curType={curType}
                    setCurType={setCurType}
                />

                <LightEnhance
                    featureType={ImagePanelFeatureType.ADJUST_LIGHT}
                    curType={curType}
                    setCurType={setCurType}
                    // onReset={onResetHandle}
                    onSlider={onSliderHandle}
                    trackFunc={trackFunc(ImagePanelFeatureType.ADJUST_LIGHT)}
                />
                <ColorEnhance
                    featureType={ImagePanelFeatureType.ADJUST_COLOR}
                    curType={curType}
                    setCurType={setCurType}
                    // onReset={onResetHandle}
                    onSlider={onSliderHandle}
                    trackFunc={trackFunc(ImagePanelFeatureType.ADJUST_COLOR)}
                />
                <ParticularsEnhance
                    featureType={ImagePanelFeatureType.ADJUST_PARTICULARS}
                    curType={curType}
                    setCurType={setCurType}
                    // onReset={onResetHandle}
                    onSlider={onSliderHandle}
                    trackFunc={trackFunc(ImagePanelFeatureType.ADJUST_PARTICULARS)}
                />
                <PosterizeEnhance
                    featureType={ImagePanelFeatureType.ADJUST_POSTERIZE}
                    curType={curType}
                    setCurType={setCurType}
                    // onReset={onResetHandle}
                    onSlider={onSliderHandle}
                    // onTab={onTabHandle}
                    trackFunc={trackFunc(ImagePanelFeatureType.ADJUST_POSTERIZE)}
                />
                <HslEnhance
                    featureType={ImagePanelFeatureType.ADJUST_HSL}
                    curType={curType}
                    setCurType={setCurType}
                    // onReset={onResetHandle}
                    onSlider={onSliderHandle}
                    trackFunc={trackFunc(ImagePanelFeatureType.ADJUST_HSL)}
                />
            </AdjustItemWrap>
        </div>
    );
};

export default React.memo(ImageAdjust);
